import { ActionButton, Buttons, CancelButton, Checkbox, Icon, Level, ModalCard } from '~/components';
import { useApi, useIntegrations, useToast, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '~/styles';

const P = styled.p`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const WarningIcon = styled(Icon)`
  font-size: 2.5rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 1rem;
`;

const Warning = ({ children }) => {
  return (
    <Level margin="1rem 0">
      <Level.Item narrow>
        <WarningIcon icon="exclamation-triangle" color={colors.warning} />
      </Level.Item>
      <Level.Item>{children}</Level.Item>
    </Level>
  );
};

function PublishInvoiceDialog({ invoiceId, resolve, onClose }) {
  const { workspace } = useWorkspace();
  const integrations = useIntegrations();
  const [lineItemStatus, setLineItemStatus] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [saveToQuickBooks, setSaveToQuickBooks] = useState(() => integrations.qbo);
  const [saveToXero, setSaveToXero] = useState(() => !!integrations.xero);
  const [accountingIntegration, setAccountingIntegration] = useState({ isReady: false, data: null, error: null });
  const [{ isSubmitting, saved }, form] = useForm();
  const toast = useToast();
  const api = useApi();

  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).invoices(invoiceId).getLineItemStatus();

      const { data: invoice } = await api.www.workspaces(workspace.id).invoices(invoiceId).get();

      setLineItemStatus(data);
      setInvoice(invoice);
    })();
  }, [api, workspace.id, invoiceId]);

  useEffect(() => {
    if (!invoice || !integrations.xero) return;
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).invoices(invoiceId).accountingIntegration();

        setAccountingIntegration({ isReady: true, data: data.xeroConnection, error: null });
      } catch (error) {
        setAccountingIntegration({
          isReady: true,
          data: invoice.xeroConnection,
          error: error.message ?? 'Unable to validate accounting integration.',
        });
      }
    })();
  }, [api, invoice, workspace, invoiceId, integrations.xero]);

  const handleSubmit = async () => {
    form.submit();

    let result;

    try {
      const { data } = await api.www.workspaces(workspace.id).invoices(invoiceId).publish();
      result = data;
    } catch (error) {
      toast.error(error.message ?? 'An error has occurred. The invoice has not been published. Please try again.');
      form.done();
      return;
    }

    if (saveToQuickBooks) {
      try {
        const { data } = await api.www.workspaces(workspace.id).invoices(invoiceId).saveToQuickBooks();

        result = data.invoice;
      } catch (error) {
        toast.error(
          error.message ??
            'An error has occurred. The invoice has not been saved to QuickBooks. Please review the QuickBooks integration settings and try again.',
        );
      }
    } else if (saveToXero && accountingIntegration.data) {
      try {
        const { data } = await api.www.workspaces(workspace.id).invoices(invoiceId).saveToXero();

        result = data.invoice;
      } catch (error) {
        toast.error(
          error.message ??
            'An error has occurred. The invoice has not been saved to Xero. Please review the Xero integration settings and try again.',
        );
      }
    }

    await resolve(result);
    form.save();
    toast.success(`Invoice has been published with number #${result.number}.`);
  };

  const handleClose = () => {
    onClose();
  };

  const showLineAmountMismatch = invoice?.lines.some(
    (line) => !!line.transactionType && line.transactionAmount !== line.amount,
  );

  const isPurchaseOrderOverBudget = invoice?.total > invoice?.purchaseOrder?.remainingAmount;

  if (!invoice) return null;
  if (!lineItemStatus) return null;

  return (
    <ModalCard title="Publish Invoice" onClose={handleClose}>
      <ModalCard.Body>
        <P>
          Are you sure you want to publish this invoice? Once an invoice is published, it cannot be changed unless it is
          unpublished.
        </P>
        {(lineItemStatus.unapprovedTime || lineItemStatus.unapprovedExpenses) && (
          <Warning>
            There are unapproved time and/or expense entries associated with this invoice. Those will be automatically
            approved when this invoice is published.
          </Warning>
        )}

        {lineItemStatus.runningTimer && (
          <Warning>
            There are one or more timers running that are associated with this invoice. Those timers will be stopped
            when this invoice is approved.
          </Warning>
        )}

        {showLineAmountMismatch && (
          <Warning>
            One or more rows on this invoice has an amount that differs from the amount that Ruddr is expecting. Any
            such rows are indicated by a warning sign on the row.
          </Warning>
        )}

        {isPurchaseOrderOverBudget && (
          <Warning>This invoice will exceed the available amount on the purchase order.</Warning>
        )}

        {accountingIntegration.error && <Warning>{accountingIntegration.error}</Warning>}
      </ModalCard.Body>

      <ModalCard.Footer>
        <Level>
          {integrations.qbo && (
            <Level.Item>
              <Checkbox
                label="Save to QuickBooks"
                checked={saveToQuickBooks}
                onChange={() => setSaveToQuickBooks(!saveToQuickBooks)}
              />
            </Level.Item>
          )}

          {!!integrations.xero && accountingIntegration.data && (
            <Level.Item>
              <CheckboxContainer>
                <Checkbox
                  label="Save to Xero"
                  subText={accountingIntegration.data.name}
                  checked={saveToXero}
                  onChange={() => setSaveToXero(!saveToXero)}
                />
              </CheckboxContainer>
            </Level.Item>
          )}

          <Level.Item>
            <Buttons align="right">
              <CancelButton onClick={handleClose}>Cancel</CancelButton>
              <ActionButton
                isLoading={isSubmitting}
                ok={saved}
                disabled={accountingIntegration.error}
                data-testid="publish_invoice_confirm"
                onClick={handleSubmit}>
                Publish
              </ActionButton>
            </Buttons>
          </Level.Item>
        </Level>
      </ModalCard.Footer>
    </ModalCard>
  );
}

export default PublishInvoiceDialog;
